import React from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import * as userAPI from "../../../api/user";
import { LoadingOverlay } from "../../../common/Loading/index";
import { Form } from "../../../forms/Form";
import { FormButtonLink } from "../../../forms/FormButtonLink";
import { FormHelpLink } from "../../../forms/FormHelpLink";
import { FormInputWithoutFormatting as FormInput } from "../../../forms/FormInputWithoutFormatting";
import { FormSubmit } from "../../../forms/FormSubmit";
import { NonFieldErrors } from "../../../forms/NonFieldErrors";
import { IWebPageURL, isoWebPageURL } from "../../../models/nominals";
import { updateGTMUserHash } from "../../../utils/analytics";
import { navigateToURL } from "../../../utils/urls";

interface IProps {
    nextURL?: IWebPageURL;
    onAfterLoad?: () => void;
    onAfterSave?: () => void;
}

export const LoginForm = (props: IProps): React.ReactNode => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [server, setServer] = React.useState({
        username: "",
        email: "",
    });
    const [client, setClient] = React.useState({
        email: "",
        password: "",
    });
    const [errors, setErrors] = React.useState({
        non_field_errors: [] as string[],
        email: [] as string[],
        password: [] as string[],
    });
    const [helptext, setHelptext] = React.useState<{
        email?: string;
    }>({});

    React.useEffect(() => {
        const loadCurrentUser = async () => {
            const user = await userAPI.load();
            setServer({
                username: user.username,
                email: user.email,
            });
            setClient((prevClient) => ({
                ...prevClient,
                email: user.email,
            }));
            if (props.onAfterLoad) {
                props.onAfterLoad();
            }
        };
        void loadCurrentUser();
    }, []);

    const onEmailChange = (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const email = input.value;
        setClient((prevClient) => ({
            ...prevClient,
            email: email,
        }));
    };

    const onPasswordChange = (event: React.FormEvent<HTMLInputElement>) => {
        const password = event.currentTarget.value;
        setClient((prevClient) => ({
            ...prevClient,
            password: password,
        }));
    };

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            const data = await userAPI.login(
                client.email,
                client.password,
                props.nextURL,
            );
            // Reload current user on success; render error message on failure
            if (data.redirect) {
                navigateToURL(data.redirect);
                return;
            }
            setIsSubmitting(false);
            setErrors({
                non_field_errors: [],
                email: [],
                password: [],
            });
            setHelptext({
                email: data.message,
            });
        } catch (error) {
            const data =
                error instanceof Error && "response" in error
                    ? (
                          error as {
                              response: { body: Record<string, string[]> };
                          }
                      ).response.body
                    : {};
            setIsSubmitting(false);
            setErrors({
                non_field_errors: data.non_field_errors || [],
                email: data.email || [],
                password: data.password || [],
            });
            setHelptext({
                email: "",
            });
        }
        updateGTMUserHash(client.email);
        if (props.onAfterSave) {
            props.onAfterSave();
        }
    };

    const buildLoginForm = () => {
        return (
            <Form className="js-kuid-form" onSubmit={onSubmit}>
                <NonFieldErrors
                    id="login_non_field"
                    errors={errors.non_field_errors}
                />
                <FormInput
                    id="account_email"
                    name="account_email"
                    type="email"
                    errors={errors.email}
                    placeholder={t`Email Address`}
                    value={client.email}
                    helpText={helptext.email}
                    onChange={onEmailChange}
                    required={true}
                    disabled={isSubmitting}
                    autoComplete="email"
                />
                <FormInput
                    id="account_password"
                    name="account_password"
                    type="password"
                    errors={errors.password}
                    placeholder={t`Password`}
                    value={client.password}
                    onChange={onPasswordChange}
                    disabled={isSubmitting}
                    autoComplete="current-password"
                />
                <FormHelpLink
                    href={isoWebPageURL.wrap(reverse("password-reset"))}
                    helpText={t`Forgot password?`}
                />
                <FormSubmit
                    value={t`Log in`}
                    className="button button--full-width"
                    disabled={isSubmitting}
                />
                <FormButtonLink
                    href={isoWebPageURL.wrap(reverse("customer:register"))}
                    text={t`Or, create account`}
                />
            </Form>
        );
    };

    if (!server.username) {
        return <LoadingOverlay visuallyHiddenText={t`Loading…`} />;
    }
    return buildLoginForm();
};
